/**
 * Event names wound-scribe publishes for manual uploads. They must match
 * MANUAL_AUDIO_NOTIFICATION_EVENT in wound-scribe.
 */
export const MANUAL_AUDIO_EVENTS = {
  completed: 'MANUAL_AUDIO_TRANSCRIPTION_COMPLETED',
  failed: 'MANUAL_AUDIO_TRANSCRIPTION_FAILED',
  summary_completed: 'ENCOUNTER_SUMMARY_COMPLETED',
  summary_failed: 'ENCOUNTER_SUMMARY_FAILED',
} as const;

export type ManualAudioNotice = {
  id: number;
  severity: 'info' | 'success' | 'error';
  message: string;
  sessionId?: string;
  /** Encounter page to reopen, without the WoundScribe tab. */
  returnPath?: string;
};

type ManualAudioNoticeInput = Omit<ManualAudioNotice, 'id'>;
type Listener = (notice: ManualAudioNotice) => void;

const listeners = new Set<Listener>();
let nextNoticeId = 0;

/** Shows a notice in the app-level snackbar. Safe to call from any screen. */
export function publishManualAudioNotice(input: ManualAudioNoticeInput): void {
  nextNoticeId += 1;
  const notice: ManualAudioNotice = { ...input, id: nextNoticeId };
  listeners.forEach((listener) => listener(notice));
}

export function subscribeToManualAudioNotices(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const PENDING_KEY = 'woundscribe-pending-manual-audio';
const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

type PendingSession = {
  sessionId: string;
  addedAt: number;
  returnPath?: string;
};

function isPendingSession(item: unknown): item is PendingSession {
  if (!item || typeof item !== 'object') {
    return false;
  }
  const record = item as Record<string, unknown>;
  const returnPathOk =
    record.returnPath === undefined || typeof record.returnPath === 'string';
  return (
    typeof record.sessionId === 'string' &&
    typeof record.addedAt === 'number' &&
    returnPathOk &&
    Date.now() - record.addedAt < PENDING_TTL_MS
  );
}

function readPending(): PendingSession[] {
  try {
    const raw = globalThis.localStorage?.getItem(PENDING_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isPendingSession);
  } catch {
    return [];
  }
}

function writePending(items: PendingSession[]): void {
  try {
    globalThis.localStorage?.setItem(PENDING_KEY, JSON.stringify(items));
  } catch {
    // Storage can be unavailable or full. Recovery is best effort.
  }
}

/**
 * Remembers an uploaded session until its outcome is seen, including the
 * encounter page to reopen if the user leaves before the transcript is ready.
 */
export function addPendingManualAudioSession(
  sessionId: string,
  returnPath?: string
): void {
  const trimmed = sessionId?.trim();
  if (!trimmed) {
    return;
  }
  const path = returnPath?.trim();
  const others = readPending().filter((item) => item.sessionId !== trimmed);
  writePending([
    ...others,
    {
      sessionId: trimmed,
      addedAt: Date.now(),
      ...(path ? { returnPath: path } : {}),
    },
  ]);
}

export function removePendingManualAudioSession(sessionId: string): void {
  writePending(readPending().filter((item) => item.sessionId !== sessionId));
}

export function getPendingManualAudioSessionIds(): string[] {
  return readPending().map((item) => item.sessionId);
}

/** Encounter path saved with this upload, if the user was on that page. */
export function getPendingManualAudioReturnPath(
  sessionId: string
): string | undefined {
  return readPending().find((item) => item.sessionId === sessionId)?.returnPath;
}

// ---------------------------------------------------------------------------
// SOAP note ready notifications (upload sessions only — live sessions use the
// recovery-fetch path in useSoapNoteSummary because the socket is disconnected
// before the LLM finishes).
// ---------------------------------------------------------------------------

export type SoapNoteReadyStatus = 'summary_completed' | 'summary_failed';

type SoapNoteReadyListener = (sessionId: string, status: SoapNoteReadyStatus) => void;

const soapNoteListeners = new Set<SoapNoteReadyListener>();

/** Called by ManualAudioNotifications when an ENCOUNTER_SUMMARY_* event arrives. */
export function publishSoapNoteReady(sessionId: string, status: SoapNoteReadyStatus): void {
  soapNoteListeners.forEach((l) => l(sessionId, status));
}

export function subscribeToSoapNoteReady(listener: SoapNoteReadyListener): () => void {
  soapNoteListeners.add(listener);
  return () => {
    soapNoteListeners.delete(listener);
  };
}
